import React, { ReactElement } from 'react';
import { format } from 'date-fns';
import StatusSelector from '../../../Common/StatusSelector/StatusSelector';
import { Task } from './TaskCard.types';
import { Option } from '@Types/common';
import './TaskCard.scss';

interface TaskCardProps {
    task: Task;
    statuses: Option[];
    onTaskClick: (taskId: number) => void;
    onStatusChange?: (taskId: number, statusId: number) => void;
    showAvatar?: boolean;
}

const TaskCard = ({ task, statuses, onTaskClick, onStatusChange, showAvatar = true }: TaskCardProps): ReactElement => {
    const getStatusTag = () => {
        if (task.isPastDue) {
            return { label: 'OVERDUE', className: 'tag--overdue' };
        }
        if (task.isMilestone) {
            return { label: 'MILESTONE', className: 'tag--milestone' };
        }
        if (task.coreStatusName === 'Ready to Start' || task.statusName === 'Ready') {
            return { label: 'READY TO START', className: 'tag--ready' };
        }
        if (task.statusName === 'At Risk') {
            return { label: 'AT RISK', className: 'tag--at-risk' };
        }
        return null;
    };

    const getCardBorderClass = () => {
        if (task.isPastDue) return 'card--overdue';
        if (task.isMilestone) return 'card--milestone';
        if (task.coreStatusName === 'Ready to Start' || task.statusName === 'Ready') return 'card--ready';
        if (task.statusName === 'At Risk') return 'card--at-risk';
        return 'card--default';
    };

    const formatDueDateInfo = () => {
        if (task.daysUntilDue === 0) {
            return 'Due today';
        } else if (task.daysUntilDue > 0) {
            return `Due ${task.daysUntilDue === 1 ? 'tomorrow' : `in ${task.daysUntilDue} days`}`;
        } else {
            return `Due ${Math.abs(task.daysUntilDue)} day${Math.abs(task.daysUntilDue) === 1 ? '' : 's'} ago`;
        }
    };

    const getAvatarInitials = (name: string) => {
        return name.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase();
    };

    const getAvatarColor = (taskId: number) => {
        const colors = ['#6c5ce7', '#00b894', '#fdcb6e', '#e17055', '#74b9ff', '#a29bfe'];
        return colors[taskId % colors.length];
    };

    const statusTag = getStatusTag();
    const cardBorderClass = getCardBorderClass();

    const handleStatusChange = (statusId: number) => {
        if (onStatusChange) {
            onStatusChange(task.taskId, statusId);
        }
    };

    const handleCardClick = () => {
        onTaskClick(task.taskId);
    };

    const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTaskClick(task.taskId);
        }
    };

    return (
        <div
            className={`task-card ${cardBorderClass}`}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Open task: ${task.taskName}`}
        >
            <div className="task-card__header">
                <div className="task-card__left">
                    {statusTag && (
                        <span className={`task-card__status-tag ${statusTag.className}`}>
                            {statusTag.label}
                        </span>
                    )}
                    {task.hasPrerequisites && (
                        <span className="task-card__prerequisites-text">
                            {'Has prerequisites'}
                        </span>
                    )}
                </div>
                <div className="task-card__right">
                    <div className="task-card__status-wrapper" onClick={(e) => e.stopPropagation()}>
                        {task.statusId !== null && task.statusId !== undefined ? (
                            <StatusSelector
                                selectedStatusId={task.statusId}
                                statuses={statuses}
                                onChange={handleStatusChange}
                                className="task-card__status-selector"
                            />
                        ) : (
                            <div className="task-card__status-indicators">
                                {task.statusName && (
                                    <div className="task-card__status-badge">
                                        <span className={`status-badge status-${task.statusName?.toLowerCase().replace(/\s+/g, '-')}`}>
                                            {task.statusName}
                                        </span>
                                    </div>
                                )}
                                <div className="task-card__urgency-indicator">
                                    <span className={`urgency-badge urgency-${task.urgencyLevel?.toLowerCase()}`}>
                                        {task.urgencyLevel || 'Normal'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {showAvatar && (
                        <div className="task-card__avatar" style={{ backgroundColor: getAvatarColor(task.taskId) }}>
                            {getAvatarInitials(task.taskName)}
                        </div>
                    )}
                    <i className="fas fa-external-link-alt task-card__external-link"></i>
                </div>
            </div>

            <div className="task-card__content">
                <h3 className="task-card__title">{task.taskName}</h3>

                <div className="task-card__department">{task.departmentName || 'General'}</div>

                <div className="task-card__location-date">
                    {task.locationName ? `${task.locationName}${task.locationAbbreviation ? ` (${task.locationAbbreviation})` : ''} • ` : ''}{formatDueDateInfo()}
                </div>

                {task.startDate && (
                    <div className="task-card__start-date">
                        <strong>Start Date:</strong> {format(new Date(task.startDate), 'MMM dd, yyyy')}
                    </div>
                )}

            </div>
        </div>
    );
};

export default TaskCard;